import { useEffect } from 'react';
import { useBackend } from '@/services/backend';
import { onCompletion, startCompletionTracking, stopCompletionTracking } from '@/stores/completionStore';
import { startDevServerTracking, stopDevServerTracking } from '@/stores/devServerStore';
import { useProjectStore } from '@/stores/projectStore';
import { useTerminalStore } from '@/stores/terminalStore';
import { useUIStore } from '@/stores/uiStore';
import { getProjectTerminalSettings } from '@/lib/projectSettings';
import { playNotificationSound } from '@/lib/notificationSound';
import { showToast } from '@/components/ui/Toast';
import { logger } from '@/lib/logger';

/**
 * Headless component that bridges terminal completion events to user notifications.
 * Rendered once in App.tsx.
 */
export default function TerminalCompletionNotifier() {
  const backend = useBackend();

  // Start/stop completion tracking
  useEffect(() => {
    startCompletionTracking(backend);
    return () => stopCompletionTracking();
  }, [backend]);

  // Start/stop dev-server URL detection (powers BrowserTab auto-open).
  // Idempotent — re-mounts don't double-subscribe.
  useEffect(() => {
    startDevServerTracking(backend);
    return () => stopDevServerTracking();
  }, [backend]);

  // Notify when run sessions (via RunButton) exit
  useEffect(() => {
    const unsub = backend.terminal.onExit((id: number) => {
      const store = useTerminalStore.getState();

      // Check if this is a run session for any project
      let runProjectPath: string | null = null;
      store.runSessionIds.forEach((sessionId: number, projectPath: string) => {
        if (sessionId === id) runProjectPath = projectPath;
      });
      if (!runProjectPath) return;

      // Global DND check
      if (useUIStore.getState().isDndActive()) return;

      // Find project for name and settings
      const projects = useProjectStore.getState().projects;
      const project = projects.find((p) => p.root_path === runProjectPath);
      const projectName = project?.name || (runProjectPath as string).split(/[/\\]/).pop() || 'Project';

      const termSettings = getProjectTerminalSettings(project?.settings ?? null);
      if (termSettings.notificationSound === 'none') return;

      playNotificationSound(termSettings.notificationSound);
      showToast(`Build completed in ${projectName}`, 'success');

      if (document.hidden && 'Notification' in window && Notification.permission === 'granted') {
        new Notification('Build Completed', {
          body: `Build finished in ${projectName}.`,
          tag: `run-${runProjectPath}`,
        });
      }

      logger.debug('CompletionNotifier', 'Run session exited for', projectName);
    });
    return unsub;
  }, [backend]);

  // Subscribe to completion events
  useEffect(() => {
    return onCompletion((projectPath) => {
      // Global DND check
      if (useUIStore.getState().isDndActive()) {
        logger.debug('CompletionNotifier', 'Suppressed notification (DND active)');
        return;
      }

      // Find the project by path to get its name and settings
      const projects = useProjectStore.getState().projects;
      const project = projects.find((p) => p.root_path === projectPath);
      const projectName = project?.name || projectPath.split(/[/\\]/).pop() || 'Project';

      // Get notification sound from project settings
      const termSettings = getProjectTerminalSettings(project?.settings ?? null);
      const soundId = termSettings.notificationSound;

      // "none" = suppress ALL notifications (sound + toast + browser)
      if (soundId === 'none') {
        logger.debug('CompletionNotifier', 'Suppressed notification for', projectName, '(sound: none)');
        return;
      }

      // Determine agent name from session title
      const allSessions = Array.from(useTerminalStore.getState().sessions.values());
      const agentSession = allSessions.find(
        (s) => s.projectPath === projectPath && s.isAgent && s.status === 'active',
      );
      const agentName = agentSession?.title || 'Agent';

      // Play sound
      playNotificationSound(soundId);

      // Show in-app toast
      showToast(`${agentName} finished in ${projectName}`, 'success');

      // Browser notification if app is backgrounded
      if (document.hidden && 'Notification' in window) {
        if (Notification.permission === 'granted') {
          new Notification('Agent Completed', {
            body: `${agentName} finished in ${projectName}.`,
            tag: `completion-${projectPath}`,
          });
        } else if (Notification.permission !== 'denied') {
          Notification.requestPermission().then((perm) => {
            if (perm === 'granted') {
              new Notification('Agent Completed', {
                body: `${agentName} finished in ${projectName}.`,
                tag: `completion-${projectPath}`,
              });
            }
          });
        }
      }

      logger.debug('CompletionNotifier', 'Notified completion for', agentName, 'in', projectName, 'sound:', soundId);
    });
  }, []);

  return null;
}
